import React, { useState } from "react";
import { Drawer, IconButton, Badge, Tooltip } from "@material-ui/core";
import CloseIcon from "@mui/icons-material/Close";
import FilterListIcon from "@material-ui/icons/FilterList";
import { FilterField } from "../TripConfiguration/util"; // adjust path to match where FmsDashboard.js lives
import { createFilterOptions, Autocomplete as MuiAutocomplete, TextField as MuiTextField } from "@mui/material";


const statusColors = {
    RUNNING: { color: "#2e7d32", bg: "#2e7d321a", label: "Running", icon: "●" },
    STOP: { color: "#d32f2f", bg: "#d32f2f1a", label: "Stop", icon: "■" },
    IDLE: { color: "#ed6c02", bg: "#ed6c021a", label: "Idle", icon: "◆" },
    OFFLINE: { color: "#0288d1", bg: "#0288d11a", label: "Offline", icon: "○" },
};

const vehicleFilterOptions = createFilterOptions({
    stringify: (option) => `${option.name || ""} ${option.imeiNo || ""}`,
});

export default function FmsFilterDrawer({
    siteOptions,
    siteFilter,
    setSiteFilter,
    vehicleTypeOptions,
    vehicleTypeFilter,
    setVehicleTypeFilter,
    vehicleOptions,
    vehicleFilter,
    setVehicleFilter,
    statusOptions,
    statusFilter,
    setStatusFilter,
    onApply, // optional — called after Apply, e.g. to trigger fetchData
}) {
    const [open, setOpen] = useState(false);

    const activeCount = [siteFilter, vehicleTypeFilter, vehicleFilter, statusFilter].filter(
        (f) => f && f.value != null
    ).length;

    const handleReset = () => {
        setSiteFilter(siteOptions[0]);
        setVehicleTypeFilter(vehicleTypeOptions[0]);
        setVehicleFilter(vehicleOptions[0]);
        setStatusFilter(statusOptions[0]);
    };

    return (
        <>
            {/* ── Icon-only filter button (matches TripFilterDrawer) ── */}
            <Tooltip title="Filters" arrow>
                <Badge
                    badgeContent={activeCount}
                    invisible={activeCount === 0}
                    sx={{
                        "& .MuiBadge-badge": {
                            background: "#ef4444",
                            color: "#fff",
                            fontSize: 10,
                            fontWeight: 700,
                            minWidth: 18,
                            height: 18,
                            borderRadius: "9px",
                            border: "2px solid #fff",
                        },
                    }}
                >
                    <button
                        onClick={() => setOpen(true)}
                        aria-label="Open FMS filters"
                        style={{
                            width: 40,
                            height: 40,
                            borderRadius: 10,
                            background: "linear-gradient(135deg, #082D6B 0%, #0F4C9A 100%)",
                            color: "#fff",
                            border: "none",
                            cursor: "pointer",
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            boxShadow: "0 4px 14px rgba(8, 45, 107, 0.3)",
                            transition: "all 0.25s ease",
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.transform = "translateY(-1px)";
                            e.currentTarget.style.boxShadow = "0 6px 20px rgba(8, 45, 107, 0.4)";
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.transform = "translateY(0)";
                            e.currentTarget.style.boxShadow = "0 4px 14px rgba(8, 45, 107, 0.3)";
                        }}
                    >
                        <FilterListIcon style={{ fontSize: 20 }} />
                    </button>
                </Badge>
            </Tooltip>

            <Drawer
                anchor="right"
                open={open}
                onClose={() => setOpen(false)}
                transitionDuration={250}
                PaperProps={{
                    style: {
                        width: 380,
                        height: "calc(100vh - 100px)",
                        background: "#f8faff",
                        borderTopLeftRadius: 20,
                        borderBottomLeftRadius: 20,
                        boxShadow: "-8px 0 40px rgba(0,0,0,0.1)",
                        border: "none",
                        marginTop: 75,
                        display: "flex",
                        flexDirection: "column",
                        overflow: "hidden",
                    },
                }}
            >
                {/* Header */}
                <div
                    style={{
                        background: "#082D6B",
                        padding: "14px 20px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        flexShrink: 0,
                    }}
                >
                    <span
                        style={{
                            color: "#fff",
                            fontWeight: 700,
                            fontSize: "1rem",
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                        }}
                    >
                        <FilterListIcon style={{ fontSize: 18 }} />
                        Filters
                    </span>
                    <IconButton size="small" onClick={() => setOpen(false)} style={{ color: "#fff" }}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </div>

                {/* Body */}
                <div
                    style={{
                        flex: 1,
                        overflowY: "auto",
                        padding: "20px 20px 24px",
                        display: "flex",
                        flexDirection: "column",
                        gap: 18,
                    }}
                >
                    <FilterField label="Site">
                        <MuiAutocomplete
                            size="small"
                            options={siteOptions}
                            getOptionLabel={(option) => option.name}
                            value={siteFilter}
                            isOptionEqualToValue={(option, value) => option.name === value.name}
                            onChange={(e, newValue) => setSiteFilter(newValue || siteOptions[0])}
                            sx={{ width: "100%" }}
                            renderInput={(params) => (
                                <MuiTextField
                                    {...params}
                                    placeholder="Site"
                                    variant="outlined"
                                    sx={{
                                        "& .MuiOutlinedInput-root": {
                                            height: 40,
                                            borderRadius: "10px",
                                            backgroundColor: "#fff",
                                            fontSize: 13,
                                            "& fieldset": { borderColor: "#d1d5db" },
                                            "&:hover fieldset": { borderColor: "#9ca3af" },
                                            "&.Mui-focused fieldset": { borderColor: "#2563eb" },
                                        },
                                    }}
                                />
                            )}
                        />
                    </FilterField>

                    <FilterField label="Vehicle Type">
                        <MuiAutocomplete
                            size="small"
                            options={vehicleTypeOptions}
                            getOptionLabel={(option) => option.name}
                            value={vehicleTypeFilter}
                            isOptionEqualToValue={(option, value) => option.name === value.name}
                            onChange={(e, newValue) => setVehicleTypeFilter(newValue || vehicleTypeOptions[0])}
                            sx={{ width: "100%" }}
                            renderInput={(params) => (
                                <MuiTextField
                                    {...params}
                                    placeholder="Vehicle Type"
                                    variant="outlined"
                                    sx={{
                                        "& .MuiOutlinedInput-root": {
                                            height: 40,
                                            borderRadius: "10px",
                                            backgroundColor: "#fff",
                                            fontSize: 13,
                                            "& fieldset": { borderColor: "#d1d5db" },
                                            "&:hover fieldset": { borderColor: "#9ca3af" },
                                            "&.Mui-focused fieldset": { borderColor: "#2563eb" },
                                        },
                                    }}
                                />
                            )}
                        />
                    </FilterField>

                    <FilterField label="Vehicle">
                        <MuiAutocomplete
                            size="small"
                            options={vehicleOptions}
                            getOptionLabel={(option) => option.name}
                            value={vehicleFilter}
                            filterOptions={vehicleFilterOptions}
                            isOptionEqualToValue={(option, value) => option.name === value.name}
                            onChange={(e, newValue) => setVehicleFilter(newValue || vehicleOptions[0])}
                            renderOption={(props, option) => {
                                const s = statusColors[option.runningStatus];
                                return (
                                    <li {...props} key={option.name}>
                                        <div
                                            style={{
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "space-between",
                                                width: "100%",
                                                gap: 10,
                                            }}
                                        >
                                            <span style={{ display: "flex", flexDirection: "column", lineHeight: 1.2 }}>
                                                <span style={{ fontSize: 13.5, fontWeight: 600, color: "#1f2937" }}>
                                                    {option.name}
                                                </span>
                                                {option.imeiNo && (
                                                    <span style={{ fontSize: 11, color: "#6b7280" }}>
                                                        IMEI: {option.imeiNo}
                                                    </span>
                                                )}
                                            </span>
                                            {s && (
                                                <span
                                                    style={{
                                                        fontSize: 11,
                                                        fontWeight: 700,
                                                        color: s.color,
                                                        background: s.bg,
                                                        borderRadius: 20,
                                                        padding: "2px 10px",
                                                        letterSpacing: "0.2px",
                                                        whiteSpace: "nowrap",
                                                    }}
                                                >
                                                    {s.label}
                                                </span>
                                            )}
                                        </div>
                                    </li>
                                );
                            }}
                            sx={{ width: "100%" }}
                            renderInput={(params) => (
                                <MuiTextField
                                    {...params}
                                    placeholder="Vehicle"
                                    variant="outlined"
                                    sx={{
                                        "& .MuiOutlinedInput-root": {
                                            height: 40,
                                            borderRadius: "10px",
                                            backgroundColor: "#fff",
                                            fontSize: 13,
                                            "& fieldset": { borderColor: "#d1d5db" },
                                            "&:hover fieldset": { borderColor: "#9ca3af" },
                                            "&.Mui-focused fieldset": { borderColor: "#2563eb" },
                                        },
                                    }}
                                />
                            )}
                        />
                    </FilterField>

                    <FilterField label="Status">
                        <MuiAutocomplete
                            size="small"
                            options={statusOptions}
                            getOptionLabel={(option) => option.name}
                            value={statusFilter}
                            isOptionEqualToValue={(option, value) => option.name === value.name}
                            onChange={(e, newValue) => setStatusFilter(newValue || statusOptions[0])}
                            sx={{ width: "100%" }}
                            renderInput={(params) => (
                                <MuiTextField
                                    {...params}
                                    placeholder="Status"
                                    variant="outlined"
                                    sx={{
                                        "& .MuiOutlinedInput-root": {
                                            height: 40,
                                            borderRadius: "10px",
                                            backgroundColor: "#fff",
                                            fontSize: 13,
                                            "& fieldset": { borderColor: "#d1d5db" },
                                            "&:hover fieldset": { borderColor: "#9ca3af" },
                                            "&.Mui-focused fieldset": { borderColor: "#2563eb" },
                                        },
                                    }}
                                />
                            )}
                        />
                    </FilterField>
                </div>

                {/* Footer */}
                <div
                    style={{
                        padding: "14px 20px",
                        borderTop: "1px solid #E5E7EB",
                        display: "flex",
                        gap: 10,
                        flexShrink: 0,
                        background: "#f8faff",
                    }}
                >
                    <button
                        onClick={handleReset}
                        style={{
                            flex: 1,
                            textTransform: "none",
                            fontWeight: 600,
                            borderRadius: 8,
                            color: "#374151",
                            border: "1px solid #d1d5db",
                            background: "#fff",
                            padding: "9px 0",
                            fontSize: 13.5,
                            cursor: "pointer",
                        }}
                    >
                        Reset
                    </button>
                    <button
                        onClick={() => {
                            setOpen(false);
                            onApply?.();
                        }}
                        style={{
                            flex: 1,
                            textTransform: "none",
                            fontWeight: 700,
                            borderRadius: 8,
                            border: "none",
                            background: "linear-gradient(135deg, #082D6B 0%, #0d3a8a 100%)",
                            color: "#fff",
                            padding: "9px 0",
                            fontSize: 13.5,
                            cursor: "pointer",
                        }}
                    >
                        Apply
                    </button>
                </div>
            </Drawer>
        </>
    );
}